import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Divider,
  IconButton,
  Link,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { Navigate, useParams } from 'react-router-dom';

import PageWrapper from '../../common/PageWrapper';
import { Button } from '../../components/core';
import CopyButton from '../../components/core/CopyButton';
import { ActionDialogModal } from '../Paylinks/components/ActionDialog/modal';
import { useIsSkipifyAdmin } from '../../hooks';
import { useMerchantAwareNavigate } from '../../hooks/useMerchantAwareNavigate';
import {
  useDeleteOnboardingMutation,
  useGetCredentialsQuery,
  useGetMerchantQuery,
  useGetOnboardingQuery,
  usePatchOnboardingStepMutation,
} from '../../store/modules/merchant/api';
import type {
  MerchantExternalCredential,
  ShopifyOnboardingStep,
} from '../../store/modules/merchant/types';
import { configurationPaths } from '../../util/constants';
import { toast } from '../../util/toast';
import { ShopifyCredentialDialog } from './ShopifyCredentialDialog';
import { ShopifyDeployStep } from './ShopifyDeployStep';
import { StatusChip } from './StatusChip';
import {
  buildAppName,
  buttonPaddingSx,
  contentMaxWidthSx,
  getApiErrorMessage,
  getInstallUrl,
  getPortalEnvironment,
  getShopHandle,
  getStatusLabel,
  getStep,
  getStepLabel,
  getProvisioningStatus,
  describeTokenExpiry,
  ONBOARDING_STATUS_COLORS,
} from './utils';

const PARTNER_DASHBOARD_URL = 'https://dev.shopify.com/dashboard/129600343/apps';

type StepCardProps = {
  number: number;
  title: string;
  status: ShopifyOnboardingStep['status'];
  children: React.ReactNode;
};

const StepStatus = ({ status }: { status: ShopifyOnboardingStep['status'] }) => (
  <StatusChip label={getStatusLabel(status)} color={ONBOARDING_STATUS_COLORS[status]} />
);

const StepCard = ({ number, title, status, children }: StepCardProps) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" gap={2}>
        <Typography component="h2" fontWeight="semibold">
          {number}. {title}
        </Typography>
        <Divider sx={{ flex: 1 }} />
        <StepStatus status={status} />
      </Stack>
      <Stack spacing={2}>{children}</Stack>
    </Stack>
  </Paper>
);

const CopyValue = ({
  label,
  value,
  missingText,
}: {
  label: string;
  value?: string;
  missingText: string;
}) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
      <strong>{label}:</strong> {value ?? missingText}
    </Typography>
    {value ? (
      <CopyButton copyString={value} title={label.toLowerCase()} />
    ) : (
      <Tooltip title={missingText}>
        <span>
          <IconButton disabled size="small" aria-label={`Copy ${label}`}>
            <ContentCopy fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    )}
  </Stack>
);

const credentialConfiguration = (credential?: MerchantExternalCredential) => {
  const data = credential?.data as { configuration?: Record<string, unknown> } | undefined;
  return data?.configuration;
};

const ShopifyProvisioningDetail = () => {
  const isSkipifyAdmin = useIsSkipifyAdmin();
  const navigate = useMerchantAwareNavigate();
  const { merchantId } = useParams<{ merchantId: string }>();
  const [isCredentialDialogOpen, setIsCredentialDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [installUrlDraft, setInstallUrlDraft] = useState('');
  const [isRefreshingStatus, setIsRefreshingStatus] = useState(false);

  const skip = !merchantId;
  const {
    data: onboarding,
    isLoading: isOnboardingLoading,
    isError: isOnboardingError,
    refetch: refetchOnboarding,
  } = useGetOnboardingQuery(
    { merchantId: merchantId ?? '', systemOfOrigin: 'shopify' },
    { skip, refetchOnMountOrArgChange: true },
  );
  const { data: merchant } = useGetMerchantQuery(merchantId ?? '', { skip });
  const {
    data: credentials,
    isLoading: areCredentialsLoading,
    isError: areCredentialsError,
    refetch: refetchCredentials,
  } = useGetCredentialsQuery({ merchantId: merchantId ?? '', type: ['ecommerce'] }, { skip });
  const [patchStep, { isLoading: isPatchingStep }] = usePatchOnboardingStepMutation();
  const [deleteOnboarding, { isLoading: isDeleting }] = useDeleteOnboardingMutation();

  const existingCredential = useMemo(
    () =>
      credentials?.ecommerce?.credentials.find(
        (credential) => credential.systemOfOrigin === 'shopify',
      ),
    [credentials],
  );
  const config = credentialConfiguration(existingCredential);
  const clientId = typeof config?.clientId === 'string' ? config.clientId : undefined;
  const automationToken =
    typeof config?.appAutomationToken === 'string' ? config.appAutomationToken : undefined;
  const hasAutomationToken = Boolean(automationToken);
  const tokenExpiry = describeTokenExpiry(
    typeof config?.appAutomationTokenExpiresAt === 'string'
      ? config.appAutomationTokenExpiresAt
      : undefined,
  );
  const shopifyOnboarding = onboarding?.systemOfOrigin === 'shopify' ? onboarding : undefined;
  // The credential is loaded here, so its own expiry is authoritative. The queue has to fall back
  // to what the credential save mirrored onto the step.
  const provisioningStatus = shopifyOnboarding
    ? getProvisioningStatus(shopifyOnboarding, Boolean(tokenExpiry?.isExpired))
    : undefined;
  const installUrl = shopifyOnboarding ? getInstallUrl(shopifyOnboarding) : undefined;
  const shopDomain = onboarding?.externalPlatformId ?? '';
  const appName = shopDomain
    ? buildAppName(getShopHandle(shopDomain), getPortalEnvironment())
    : 'ella-shopify';
  const deployStep = getStep(shopifyOnboarding, 'app_deploy');
  const deployStatus = deployStep?.status ?? 'pending';
  const appInitDone = getStep(shopifyOnboarding, 'app_init')?.status === 'done';
  const distributionDone = getStep(shopifyOnboarding, 'distribution')?.status === 'done';
  const oauthComplete = getStep(shopifyOnboarding, 'oauth_complete')?.status === 'done';
  const deployUnlocked = distributionDone;
  const embedUrl = clientId
    ? `https://${shopDomain}/admin/themes/current/editor?context=apps&activateAppId=${clientId}/sdk-embed`
    : undefined;

  useEffect(() => {
    setInstallUrlDraft(installUrl ?? '');
  }, [installUrl]);

  if (!isSkipifyAdmin) {
    return <Navigate to={configurationPaths.SETTINGS} replace />;
  }

  if (isOnboardingLoading) {
    return (
      <PageWrapper title="Shopify provisioning" description="Loading Shopify onboarding." isLoading>
        {null}
      </PageWrapper>
    );
  }

  if (isOnboardingError || !onboarding || !merchantId) {
    return (
      <PageWrapper title="Shopify provisioning" description="This onboarding could not be loaded.">
        <Stack sx={contentMaxWidthSx} spacing={2}>
          <Alert severity="error">
            The Shopify onboarding was not found or could not be loaded.
          </Alert>
          <Button
            text="Back to queue"
            theme="secondary"
            variant="outlined"
            sx={buttonPaddingSx}
            onClick={() => navigate(configurationPaths.ADMIN_SHOPIFY_PROVISIONING)}
          />
        </Stack>
      </PageWrapper>
    );
  }

  const markDone = async (key: ShopifyOnboardingStep['key'], meta?: Record<string, unknown>) => {
    try {
      await patchStep({
        merchantId,
        systemOfOrigin: 'shopify',
        key,
        body: { status: 'done', ...(meta ? { meta } : {}) },
      }).unwrap();
      toast.success(`${getStepLabel(key)} marked done.`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to update this onboarding step.'));
    }
  };

  const markCredentialsSaved = async (appAutomationTokenExpiresAt?: string) => {
    // app_init is written every time, even when it is already done, because its meta is where the
    // queue reads token expiry from. A rotation that leaves the token field blank passes no expiry
    // and keeps whatever is recorded.
    await patchStep({
      merchantId,
      systemOfOrigin: 'shopify',
      key: 'app_init',
      body: {
        status: 'done',
        meta: {
          ...getStep(shopifyOnboarding, 'app_init')?.meta,
          ...(appAutomationTokenExpiresAt ? { appAutomationTokenExpiresAt } : {}),
        },
      },
    }).unwrap();

    if (getStep(shopifyOnboarding, 'credentials_saved')?.status !== 'done') {
      await patchStep({
        merchantId,
        systemOfOrigin: 'shopify',
        key: 'credentials_saved',
        body: { status: 'done' },
      }).unwrap();
    }
  };

  const checkStatus = async () => {
    setIsRefreshingStatus(true);
    try {
      const [, credentialsResponse] = await Promise.all([
        refetchOnboarding(),
        refetchCredentials(),
      ]);

      // Only ecom-connector writes refreshToken/tokenExpiresAt, and only after a successful OAuth
      // exchange, so their presence is the signal that the install completed.
      const currentCredential = credentialsResponse.data?.ecommerce?.credentials.find(
        (credential) => credential.systemOfOrigin === 'shopify',
      );
      const currentConfig = credentialConfiguration(currentCredential);
      const oauthTokenPresent =
        typeof currentConfig?.refreshToken === 'string' && !!currentConfig.refreshToken;

      if (oauthTokenPresent && getStep(shopifyOnboarding, 'oauth_complete')?.status !== 'done') {
        await patchStep({
          merchantId,
          systemOfOrigin: 'shopify',
          key: 'oauth_complete',
          body: { status: 'done' },
        }).unwrap();
      }

      toast.success('Onboarding status refreshed.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to refresh onboarding status.'));
    } finally {
      setIsRefreshingStatus(false);
    }
  };

  const removeFromQueue = async () => {
    try {
      await deleteOnboarding({ merchantId, systemOfOrigin: 'shopify' }).unwrap();
      toast.success('Shopify onboarding removed from the queue.');
      navigate(configurationPaths.ADMIN_SHOPIFY_PROVISIONING);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to remove Shopify onboarding.'));
    }
  };

  return (
    <PageWrapper
      data-testid="SHOPIFY_PROVISIONING_DETAIL"
      title={`Shopify provisioning: ${merchant?.name ?? onboarding.merchantName ?? merchantId}`}
      description={shopDomain}
    >
      <Stack spacing={3}>
        <Stack
          direction="row"
          gap={1}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            bgcolor: 'background.paper',
            py: 1,
            width: '100%',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems="stretch">
            <Button
              text="Back to queue"
              theme="secondary"
              variant="outlined"
              sx={buttonPaddingSx}
              onClick={() => navigate(configurationPaths.ADMIN_SHOPIFY_PROVISIONING)}
            />
            <Button
              text="Remove from queue"
              theme="secondary"
              variant="outlined"
              sx={buttonPaddingSx}
              onClick={() => setIsDeleteDialogOpen(true)}
            />
          </Stack>
          <Button
            text="Check Status"
            sx={buttonPaddingSx}
            onClick={checkStatus}
            disabled={isRefreshingStatus}
            loading={isRefreshingStatus}
          />
        </Stack>

        <Stack spacing={3} sx={contentMaxWidthSx}>
          {/* The same verdict the queue gives, so a row and its detail page never disagree about
              where this onboarding stands. */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Status
            </Typography>
            {provisioningStatus && (
              <StatusChip label={provisioningStatus.label} color={provisioningStatus.color} />
            )}
            {provisioningStatus?.reason && (
              <Typography variant="body2" color="text.secondary">
                {provisioningStatus.reason}
              </Typography>
            )}
          </Stack>

          {areCredentialsError && (
            <Alert severity="warning">
              Shopify credentials could not be loaded. Partner-key writes are disabled until they
              can be read.
            </Alert>
          )}

          <Stack spacing={3}>
            <StepCard
              number={1}
              title={getStepLabel('app_init')}
              status={getStep(shopifyOnboarding, 'app_init')?.status ?? 'pending'}
            >
              <Typography variant="body2">
                Create the app in the{' '}
                <Link href={PARTNER_DASHBOARD_URL} target="_blank" rel="noreferrer">
                  Shopify Dev Dashboard
                </Link>
                . Use the suggested name below, then paste the Partner client ID and secret.
              </Typography>
              <CopyValue
                label="Suggested app name"
                value={appName}
                missingText="Shop domain is required"
              />
              <Button
                text={clientId ? 'Rotate Partner keys' : 'Paste Partner keys'}
                sx={buttonPaddingSx}
                onClick={() => setIsCredentialDialogOpen(true)}
                disabled={areCredentialsLoading || areCredentialsError}
              />
              <CopyValue label="Client ID" value={clientId} missingText="Save Partner keys first" />
              <Typography variant="body2">
                <strong>{getStepLabel('credentials_saved')}:</strong>{' '}
                {getStatusLabel(
                  getStep(shopifyOnboarding, 'credentials_saved')?.status ?? 'pending',
                )}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2">
                  <strong>Automation token:</strong> {hasAutomationToken ? 'Saved' : 'Not saved'}
                </Typography>
                {tokenExpiry && <StatusChip label={tokenExpiry.label} color={tokenExpiry.color} />}
              </Stack>
              {tokenExpiry?.isExpired && (
                <Alert severity="warning">
                  This automation token has expired. Generate a new one in the Shopify Dev Dashboard
                  and save it with Rotate Partner keys before deploying.
                </Alert>
              )}
            </StepCard>

            <StepCard
              number={2}
              title={getStepLabel('distribution')}
              status={getStep(shopifyOnboarding, 'distribution')?.status ?? 'pending'}
            >
              <Typography variant="body2">
                In the Shopify Dev Dashboard, choose Custom distribution and allowlist the shop
                below. Paste the install link Shopify generates once both are set.
              </Typography>
              <CopyValue
                label="Shop domain"
                value={shopDomain}
                missingText="Shop domain is unavailable"
              />
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', md: 'center' }}
              >
                <TextField
                  fullWidth
                  label="Partner-generated install link"
                  aria-label="Partner-generated install link"
                  value={installUrlDraft}
                  onChange={(event) => setInstallUrlDraft(event.target.value)}
                  disabled={distributionDone}
                />
                <Button
                  text="Mark done"
                  theme="secondary"
                  variant="outlined"
                  sx={buttonPaddingSx}
                  onClick={() => markDone('distribution', { installUrl: installUrlDraft.trim() })}
                  disabled={
                    distributionDone || !appInitDone || !installUrlDraft.trim() || isPatchingStep
                  }
                  loading={isPatchingStep}
                />
              </Stack>
              {!appInitDone && (
                <Typography color="text.secondary" variant="body2">
                  Complete Create app first.
                </Typography>
              )}
            </StepCard>

            <StepCard number={3} title={getStepLabel('app_deploy')} status={deployStatus}>
              <ShopifyDeployStep
                merchantId={merchantId}
                step={deployStep}
                clientId={clientId}
                automationToken={automationToken}
                appName={appName}
                isTokenExpired={Boolean(tokenExpiry?.isExpired)}
                isUnlocked={deployUnlocked}
                onStarted={() => refetchOnboarding()}
              />
            </StepCard>

            {/* Steps 1-3 are done by Skipify in the Dev Dashboard; everything below happens in the
                merchant's own Shopify admin. The admin still ticks these off here. */}
            <Divider>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 12 }}
              >
                Handed off to the merchant
              </Typography>
            </Divider>

            <StepCard
              number={4}
              title={getStepLabel('oauth_complete')}
              status={getStep(shopifyOnboarding, 'oauth_complete')?.status ?? 'pending'}
            >
              <Typography variant="body2">
                Install the app in the allowlisted shop using the link below, then use Check Status.
                This step completes once the connector has exchanged the OAuth token.
              </Typography>
              <CopyValue
                label="Install URL"
                value={installUrl}
                missingText="Paste the Partner-generated link on Distribution"
              />
            </StepCard>

            <StepCard
              number={5}
              title={getStepLabel('embed_enabled')}
              status={getStep(shopifyOnboarding, 'embed_enabled')?.status ?? 'pending'}
            >
              <Typography variant="body2">
                Enable the Skipify embed block in Shopify&apos;s theme editor after OAuth completes.
              </Typography>
              <CopyValue
                label="Theme editor URL"
                value={embedUrl}
                missingText="Client ID is required to build this link"
              />
              <Button
                text="Mark done"
                theme="secondary"
                variant="outlined"
                sx={buttonPaddingSx}
                onClick={() => markDone('embed_enabled')}
                disabled={
                  !oauthComplete ||
                  getStep(shopifyOnboarding, 'embed_enabled')?.status === 'done' ||
                  isPatchingStep
                }
                loading={isPatchingStep}
              />
              {!oauthComplete && (
                <Typography color="text.secondary" variant="body2">
                  Complete OAuth first.
                </Typography>
              )}
            </StepCard>
          </Stack>
          <Divider />
          <Typography variant="body2" color="text.secondary">
            The shop domain cannot be changed after this onboarding is created. Remove the queue
            entry and add it again if the binding is wrong.
          </Typography>
        </Stack>
      </Stack>

      <ShopifyCredentialDialog
        isOpen={isCredentialDialogOpen}
        merchantId={merchantId}
        shopDomain={shopDomain}
        existingCredential={existingCredential}
        onClose={() => setIsCredentialDialogOpen(false)}
        onSaved={markCredentialsSaved}
      />
      <ActionDialogModal
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onProceed={removeFromQueue}
        isSubmitting={isDeleting}
        content={{
          heading: 'Remove this Shopify onboarding?',
          helpingText: 'This soft-deletes the onboarding. You can add the merchant again later.',
          cancelButtonText: 'Cancel',
          proceedButtonText: 'Remove from queue',
        }}
      />
    </PageWrapper>
  );
};

export default ShopifyProvisioningDetail;
